import React, { useState, useEffect, useMemo } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScreenData } from "../../types/ScreenTypes";
import DateDisplay from "../elements/DateDisplay";
import MasjidHeader from "../elements/MasjidHeader";
import PrayerCountdown from "../elements/PrayerCountdown";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import BlankScreen from "../elements/BlankScreen"; // ✅ ADD this import
import {
  getNextPrayerTime,
  determineNextPrayer,
  getHighlightedPrayerIndex,
  getInProgressPrayerIndex,
} from "../../utils/prayerTimeUtils";
import {
  getSunriseTimeDisplay,
  getJummahDisplayInfo,
  getSehriTimeDisplay,
  getNoonTimeDisplay,
  formatTimeDisplay,
} from "../../utils/timeUtils";
import { formatGregorianDate, formatHijriDate } from "../../utils/dateUtils";
import "./VerticalLayout.css";
import useBackgroundImage from "../../hooks/useBackgroundImage";
import {
  PRAYER_TYPES,
  PRAYER_TIME_KEYS,
} from "../../constants/prayerConstants";
import MakroohWarning from "../elements/MakroohWarning";
import { getCurrentTimeInTimezone } from "../../utils/timeUtils";
import { count } from "console";

interface VerticalLayoutProps {
  masjidName: string;
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null;
  templateId?: string;
  hasChanges?: boolean;
  onChangesAvailable?: (available: boolean) => void;
  screenData: ScreenData;
  prayerState: PrayerState;
  countdownSeconds?: number; // ✅ Add this prop
  flashingPrayer?: string | null; // Prayer key currently flashing (e.g. "JamDhuhr")
}

export default function VerticalLayout({
  masjidName,
  prayerTimes,
  tomorrowPrayerTimes = null,
  templateId = "template1",
  screenData,
  prayerState,
  countdownSeconds,
  flashingPrayer = null,
}: VerticalLayoutProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextPrayer, setNextPrayer] = useState<number>(0);
  const [currentTimeStr, setCurrentTimeStr] = useState<string>("");
  const currentSalahHighlightColor =
    screenData.currentSalahHighlightColor || "#FF0000";
  const [currentDateStr, setCurrentDateStr] = useState<string>(() => {
    const now = getCurrentTimeInTimezone(screenData.timeZone);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  });

  const { containerStyle, backgroundStyle, overlayStyle, contentStyle } =
    useBackgroundImage(
      screenData.backgroundUrl || undefined,
      screenData.backgroundOverlayType || "gradient",
      screenData.backgroundOverlayOpacity || 0.65,
    );

  useEffect(() => {
    function updateTime() {
      const now = getCurrentTimeInTimezone(screenData.timeZone);
      setCurrentTime(now);
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
      setCurrentTimeStr(timeStr);

      // Update date string - triggers Gregorian date recalculation at midnight
      const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setCurrentDateStr((prev) => {
        if (prev !== newDateStr) {
          console.log(
            `📅 [VerticalLayout] Date changed: ${prev} → ${newDateStr}`,
          );
          return newDateStr;
        }
        return prev;
      });

      const idx = getHighlightedPrayerIndex(
        prayerTimes,
        tomorrowPrayerTimes,
        screenData,
        timeStr,
        now,
      );
      setNextPrayer(idx); // reuse state as "highlighted index"
    }
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [
    prayerTimes,
    tomorrowPrayerTimes,
    screenData.timeZone,
    screenData.fajrLength,
    screenData.dhuhrLength,
    screenData.asarLength,
    screenData.maghribLength,
    screenData.ishaLength,
    screenData.jummahLength,
  ]);

  // Bulletproof date update: Handle page visibility changes (browser tab wake-up, screen on)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        const now = getCurrentTimeInTimezone(screenData.timeZone);
        const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        setCurrentDateStr((prev) => {
          if (prev !== newDateStr) {
            console.log(
              `📅 [VerticalLayout] Date updated on visibility: ${prev} → ${newDateStr}`,
            );
            return newDateStr;
          }
          return prev;
        });
        setCurrentTime(now);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [screenData.timeZone]);

  // Bulletproof date update: Periodic sanity check every 60 seconds
  useEffect(() => {
    const sanityCheck = setInterval(() => {
      const now = getCurrentTimeInTimezone(screenData.timeZone);
      const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setCurrentDateStr((prev) => {
        if (prev !== newDateStr) {
          console.log(
            `📅 [VerticalLayout] Date corrected by sanity check: ${prev} → ${newDateStr}`,
          );
          return newDateStr;
        }
        return prev;
      });
    }, 60000);

    return () => clearInterval(sanityCheck);
  }, [screenData.timeZone]);

  // Format current date - depends on currentDateStr state which is reliably updated at midnight
  const formattedGregorianDate = React.useMemo(() => {
    const today = getCurrentTimeInTimezone(screenData.timeZone);
    const format = screenData.gregorianDateFormat || "dddd, MMMM dd, yyyy";
    return formatGregorianDate(today, format);
  }, [screenData.gregorianDateFormat, currentDateStr, screenData.timeZone]);

  const formattedIslamicDate = React.useMemo(() => {
    const dateValue = prayerState.currentIslamicDate;
    if (typeof dateValue === "string") return dateValue;
    if (dateValue && typeof dateValue === "object") {
      const day = (dateValue as any).Day || "";
      const monthName = (dateValue as any).MonthName || "";
      const year = (dateValue as any).Year || "";
      const format = screenData.hijriDateFormat || "dd MMMM yyyy";
      return formatHijriDate(
        day,
        monthName,
        year,
        format,
        screenData.customizedNames,
      );
    }
    return "--";
  }, [
    prayerState.currentIslamicDate,
    screenData.hijriDateFormat,
    screenData.customizedNames,
  ]);

  const formatTimeWithPeriod = (
    timeStr: string | null | undefined,
    use12Hour: boolean = true,
  ) => {
    if (!timeStr || timeStr === "--:--") return "--:--";
    try {
      const formatted = formatTimeDisplay(timeStr, use12Hour);
      if (
        !use12Hour ||
        (!formatted.includes("am") && !formatted.includes("pm"))
      ) {
        return formatted;
      }
      const amIndex = formatted.indexOf("am");
      const pmIndex = formatted.indexOf("pm");
      const periodIndex = amIndex >= 0 ? amIndex : pmIndex;
      if (periodIndex >= 0) {
        const timePart = formatted.substring(0, periodIndex);
        const periodPart = formatted.substring(periodIndex, periodIndex + 2);
        return (
          <>
            {timePart}
            {/* <span className="period">{periodPart}</span> */}
          </>
        );
      }
      return formatted;
    } catch {
      return "--:--";
    }
  };

  // ✅ ADD: Early return for blank screen (same as LandscapeLayout)
  if (prayerState.isScreenBlank) {
    return (
      <BlankScreen
        message={screenData.salahInProgressMessage}
        hijriDate={formattedIslamicDate}
        gregorianDate={formattedGregorianDate}
        screenData={screenData}
        showDateTime={true}
        backgroundUrl={screenData.blankScreenBackgroundUrl}
        countdownSecondsLeft={prayerState.blankScreenSecondsLeft}
        countdownTotalSeconds={prayerState.blankScreenTotalSeconds}
        countdownPrayer={prayerState.blankScreenPrayer}
      />
    );
  }

  // Helper to count valid Jummah times
  const jummahTimes = [
    prayerTimes.Jummah1,
    prayerTimes.Jummah2,
    prayerTimes.Jummah3,
    prayerTimes.Jummah4,
  ].filter((j) => j && j !== "--:--" && j !== "");

  // If there are exactly 1 jummah and noon exists, show all 4 in one row
  const showAllSpecialTimesInOneRow =
    jummahTimes.length === 1 && !!prayerTimes.Noon;

  return (
    <div
      className={`vertical-layout-container template-${templateId}`}
      style={{
        ...(containerStyle as React.CSSProperties),
        ["--current-salah-highlight-color" as any]: currentSalahHighlightColor,
      }}
    >
      {screenData.backgroundUrl && (
        <div className="background-layer" style={backgroundStyle}></div>
      )}
      {screenData.backgroundUrl && (
        <div className="overlay-layer" style={overlayStyle}></div>
      )}

      <div className="content-layer" style={contentStyle}>
        <div className="time-section">
          <div className="current-time">
            {(() => {
              if (screenData.use12HourFormat) {
                const [hours, minutes] = [
                  currentTime.getHours(),
                  currentTime.getMinutes(),
                ];
                const hour12 = hours % 12 || 12;
                const period = hours >= 12 ? "pm" : "am";
                return (
                  <>
                    {`${hour12}:${minutes
                      .toString()
                      .padStart(2, "0")}:${currentTime
                      .getSeconds()
                      .toString()
                      .padStart(2, "0")}`}
                    {/*  */}
                  </>
                );
              } else {
                return `${currentTime
                  .getHours()
                  .toString()
                  .padStart(2, "0")}:${currentTime
                  .getMinutes()
                  .toString()
                  .padStart(2, "0")}:${currentTime
                  .getSeconds()
                  .toString()
                  .padStart(2, "0")}`;
              }
            })()}
          </div>
          <DateDisplay
            hijriDate={formattedIslamicDate}
            gregorianDate={formattedGregorianDate}
          />
        </div>

        <div className="prayer-times-grid">
          {(() => {
            // Calculate in-progress prayer index once for all cards
            const inProgressIdx = getInProgressPrayerIndex(
              prayerTimes,
              screenData,
              currentTime,
            );

            return [
              {
                name: screenData.customizedNames.fajr || "Fajr",
                prayerType: PRAYER_TYPES.FAJR,
                index: 0,
                isCountdown:
                  prayerState.isCountdownActive &&
                  prayerState.countdownPrayer ===
                    screenData.customizedNames.fajr,
              },
              {
                ...(() => {
                  const jummahInfo = getJummahDisplayInfo(
                    currentTime,
                    currentTimeStr,
                    prayerTimes,
                    screenData,
                  );
                  const isJummah = jummahInfo.showJummah;
                  const jummahName = isJummah
                    ? jummahInfo.jummahNumber > 1
                      ? `${screenData.customizedNames.jummah || "Jumu'ah"} ${
                          jummahInfo.jummahNumber
                        }`
                      : screenData.customizedNames.jummah || "Jumu'ah"
                    : screenData.customizedNames.dhuhr || "Dhuhr";
                  return {
                    name: jummahName,
                    prayerType: PRAYER_TYPES.DHUHR,
                    index: 1,
                    isCountdown:
                      prayerState.isCountdownActive &&
                      (prayerState.countdownPrayer ===
                        screenData.customizedNames.dhuhr ||
                        prayerState.countdownPrayer ===
                          screenData.customizedNames.jummah),
                    isJummah: isJummah,
                    jummahInfo: jummahInfo,
                  };
                })(),
              },
              {
                name: screenData.customizedNames.asar || "Asar",
                prayerType: PRAYER_TYPES.ASAR,
                index: 2,
                isCountdown:
                  prayerState.isCountdownActive &&
                  prayerState.countdownPrayer ===
                    screenData.customizedNames.asar,
              },
              {
                name: screenData.customizedNames.maghrib || "Maghrib",
                prayerType: PRAYER_TYPES.MAGHRIB,
                index: 3,
                isCountdown:
                  prayerState.isCountdownActive &&
                  prayerState.countdownPrayer ===
                    screenData.customizedNames.maghrib,
              },
              {
                name: screenData.customizedNames.isha || "Isha",
                prayerType: PRAYER_TYPES.ISHA,
                index: 4,
                isCountdown:
                  prayerState.isCountdownActive &&
                  prayerState.countdownPrayer ===
                    screenData.customizedNames.isha,
              },
            ].map((prayer) => {
              const nextTime = getNextPrayerTime(
                prayer.prayerType,
                prayerTimes,
                tomorrowPrayerTimes,
                currentTimeStr,
              );

              // Override to force today's times if this prayer is in its jamaat window
              if (prayer.index === inProgressIdx) {
                const keys = PRAYER_TIME_KEYS[
                  prayer.prayerType as keyof typeof PRAYER_TIME_KEYS
                ] as { BEG?: string; JAM?: string };

                if (keys) {
                  nextTime.time = keys.BEG
                    ? (prayerTimes[keys.BEG as keyof PrayerTimes] as string)
                    : nextTime.time;
                  nextTime.iqama = keys.JAM
                    ? (prayerTimes[keys.JAM as keyof PrayerTimes] as string)
                    : nextTime.iqama;
                  nextTime.isToday = true;
                }
              }

              // Special handling for Jumu'ah
              if ("isJummah" in prayer && prayer.isJummah) {
                if ("jummahInfo" in prayer && prayer.jummahInfo) {
                  nextTime.iqama = prayer.jummahInfo.jummahTime;
                  nextTime.isToday = true;
                }
              }

              const isInProgress = prayer.index === inProgressIdx;

              return (
                <div
                  key={prayer.index}
                  className={`prayer-time-container${
                    nextPrayer === prayer.index ? " active" : ""
                  }${isInProgress ? " in-progress" : ""}`}
                >
                  <div className="prayer-name prayer-col-name">
                    {prayer.name}
                  </div>
                  <div className="beg-time prayer-col-beg">
                    {prayer.prayerType === PRAYER_TYPES.MAGHRIB ? (
                      <span style={{ visibility: "hidden" }}>--:--</span>
                    ) : nextTime.time ? (
                      formatTimeWithPeriod(
                        nextTime.time,
                        screenData.use12HourFormat,
                      )
                    ) : (
                      "--:--"
                    )}
                  </div>
                  <div
                    className={`jam-time prayer-col-jamat${
                      flashingPrayer &&
                      prayer.prayerType ===
                        {
                          JamFajr: "fajr",
                          JamDhuhr: "dhuhr",
                          JamAsar: "asar",
                          JamEsha: "isha",
                        }[flashingPrayer]
                        ? " salah-change-flash"
                        : ""
                    }`}
                    style={
                      flashingPrayer &&
                      prayer.prayerType ===
                        {
                          JamFajr: "fajr",
                          JamDhuhr: "dhuhr",
                          JamAsar: "asar",
                          JamEsha: "isha",
                        }[flashingPrayer]
                        ? ({
                            "--flash-speed": `${screenData.flashSalahChangesSpeed ?? 1}s`,
                            ...(screenData.flashSalahChangesColor
                              ? { color: screenData.flashSalahChangesColor }
                              : {}),
                          } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {isInProgress ? (
                      <span className="in-progress-time">
                        {formatTimeWithPeriod(
                          nextTime.iqama || "--:--",
                          screenData.use12HourFormat,
                        )}
                      </span>
                    ) : prayer.isCountdown ? (
                      <PrayerCountdown
                        prayerName={prayer.name}
                        seconds={
                          countdownSeconds ?? prayerState.countdownSeconds
                        }
                        textColor={screenData.countdownTextColor || "#FF0000"}
                      />
                    ) : (
                      formatTimeWithPeriod(
                        nextTime.iqama || "--:--",
                        screenData.use12HourFormat,
                      )
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {showAllSpecialTimesInOneRow ? (
          <div className="special-times">
            {/* Sehri End */}
            <div className="special-time-item">
              <div className="special-time-label">
                {screenData.customizedNames.sehri}
              </div>
              <div className="special-time-value">
                {(() => {
                  const sehriDisplay = getSehriTimeDisplay(
                    currentTimeStr,
                    prayerTimes.SehriEnd || "--:--",
                    tomorrowPrayerTimes?.SehriEnd,
                  );
                  return formatTimeWithPeriod(
                    sehriDisplay.time,
                    screenData.use12HourFormat,
                  );
                })()}
              </div>
            </div>
            {/* Sunrise */}
            <div className="special-time-item">
              <div className="special-time-label">
                {screenData.customizedNames.sunrise}
              </div>
              <div className="special-time-value">
                {(() => {
                  const sunriseDisplay = getSunriseTimeDisplay(
                    currentTimeStr,
                    prayerTimes.Sunrise || "--:--",
                    tomorrowPrayerTimes?.Sunrise,
                    screenData.sunriseMakroohPeriod || 0,
                  );
                  return formatTimeWithPeriod(
                    sunriseDisplay.time,
                    screenData.use12HourFormat,
                  );
                })()}
              </div>
            </div>
            {/* Noon */}
            {prayerTimes.Noon && (
              <div className="special-time-item">
                <div className="special-time-label">
                  {screenData.customizedNames.noon || "Noon"}
                </div>
                <div className="special-time-value">
                  {(() => {
                    const noonDisplay = getNoonTimeDisplay(
                      currentTimeStr,
                      prayerTimes.Noon || "--:--",
                      tomorrowPrayerTimes?.Noon,
                    );
                    return formatTimeWithPeriod(
                      noonDisplay.time || "--:--",
                      screenData.use12HourFormat,
                    );
                  })()}
                </div>
              </div>
            )}
            {/* Jummah (if present) */}
            {jummahTimes.length === 1 && (
              <div className="special-time-item">
                <div className="special-time-label">
                  {screenData.customizedNames.jummah || "Jumu'ah"}
                </div>
                <div className="special-time-value">
                  {jummahTimes[0] &&
                  typeof jummahTimes[0] === "string" &&
                  /^\d{1,2}:\d{2}/.test(jummahTimes[0])
                    ? formatTimeWithPeriod(
                        jummahTimes[0],
                        screenData.use12HourFormat,
                      )
                    : jummahTimes[0] || "--:--"}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Top row: Sehri, Sunrise, Noon */}
            <div className="special-times">
              <div className="special-time-item">
                <div className="special-time-label">
                  {screenData.customizedNames.sehri}
                </div>
                <div className="special-time-value">
                  {(() => {
                    const sehriDisplay = getSehriTimeDisplay(
                      currentTimeStr,
                      prayerTimes.SehriEnd || "--:--",
                      tomorrowPrayerTimes?.SehriEnd,
                    );
                    return formatTimeWithPeriod(
                      sehriDisplay.time,
                      screenData.use12HourFormat,
                    );
                  })()}
                </div>
              </div>
              <div className="special-time-item">
                <div className="special-time-label">
                  {screenData.customizedNames.sunrise}
                </div>
                <div className="special-time-value">
                  {(() => {
                    const sunriseDisplay = getSunriseTimeDisplay(
                      currentTimeStr,
                      prayerTimes.Sunrise || "--:--",
                      tomorrowPrayerTimes?.Sunrise,
                      screenData.sunriseMakroohPeriod || 0,
                    );
                    return formatTimeWithPeriod(
                      sunriseDisplay.time,
                      screenData.use12HourFormat,
                    );
                  })()}
                </div>
              </div>
              {prayerTimes.Noon && (
                <div className="special-time-item">
                  <div className="special-time-label">
                    {screenData.customizedNames.noon || "Noon"}
                  </div>
                  <div className="special-time-value">
                    {(() => {
                      const noonDisplay = getNoonTimeDisplay(
                        currentTimeStr,
                        prayerTimes.Noon || "--:--",
                        tomorrowPrayerTimes?.Noon,
                      );
                      return formatTimeWithPeriod(
                        noonDisplay.time || "--:--",
                        screenData.use12HourFormat,
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            {/* Bottom row: All Jummah times */}
            {jummahTimes.length > 0 && (
              <div className="special-times">
                {jummahTimes.map((jt, idx) => (
                  <div className="special-time-item" key={"jummah-" + idx}>
                    <div className="special-time-label">
                      {screenData.customizedNames.jummah || "Jumu'ah"}
                      {jummahTimes.length > 1 ? ` ${idx + 1}` : ""}
                    </div>
                    <div className="special-time-value">
                      {jt && typeof jt === "string" && /^\d{1,2}:\d{2}/.test(jt)
                        ? formatTimeWithPeriod(jt, screenData.use12HourFormat)
                        : jt || "--:--"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <MasjidHeader masjidName={masjidName} />

      {/* Makrooh warning */}
      {prayerState.isMakroohTime && (
        <MakroohWarning
          reason={prayerState.makroohReason}
          customizedNames={{
            sunrise: screenData.customizedNames.sunrise,
            noon: screenData.customizedNames.noon,
            sunset: screenData.customizedNames.sunset,
          }}
          duration={{
            sunrise: screenData.sunriseMakroohPeriod,
            zawwal: screenData.zawwalMakroohPeriod,
            sunset: screenData.sunsetMakroohPeriod,
          }}
          secondsLeft={prayerState.makroohSecondsLeft}
          showTimer={screenData.showMakroohTimer ?? true}
          countdownLabel={screenData.makroohTimerLabel ?? "Time Remaining:"}
        />
      )}

      {/* Footer */}
      {/* <div className="powered-by">
        <PoweredBy />
      </div> */}
    </div>
  );
}
